
import PropTypes from 'prop-types';
import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grow,
  Switch,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { getAgentById } from '../../../../actions/agents/get-agents.actions';
import { updateAgentInformation } from '../../../../actions/agents/change-agent-info.actions';
import { deleteAgent } from '../../../../actions/agents/delete-agent.accions';
import { useToken } from '../../../../hooks/useToken';
import { DeleteAgent } from './DeleteAgent';
import { ReassignContactsModal } from './ReassignContactsModal';
import { ROLE_OPTIONS, SUBROLE_OPTIONS } from '../constants';

export const EditAgentInformation = ({
  open,
  onClose,
  agent,
  onAgentUpdated,
  onAgentDeleted,
  agentsForReassign = [],
  onNotify,
  canEdit = true,
}) => {
  const { getTokenSilent } = useToken();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [showDelete, setShowDelete] = useState(false);
  const [showReassign, setShowReassign] = useState(false);

  const [localForm, setLocalForm] = useState({
    display_name: '',
    role: '',
    subrole: '',
    branch: '',
    agent_phone_number: '',
    agent_disponibility: '',
    is_active: false,
    email: '',
  });

  const [snackbar, setSnackbar] = useState({ open: false, severity: 'success', message: '' });

  useEffect(() => {
    const load = async () => {
      if (!open || !agent) return;
      setLoading(true);
      try {
        const token = await getTokenSilent();
        const full = await getAgentById({ id_customer: agent.id_customer || agent.idCustomer || agent.id_customer, id_agent: agent._id, token });
        setData(full || agent);
        setLocalForm({
          display_name: full?.display_name || agent.display_name || '',
          role: full?.role || agent.role || '',
          subrole: full?.subrole || agent.subrole || '',
          branch: full?.branch || agent.branch || '',
          agent_phone_number: full?.agent_phone_number || agent.agent_phone_number || '',
          agent_disponibility: full?.agent_disponibility || agent.agent_disponibility || (full?.agent_disponibility ? 'Activo' : 'Inactivo') || '',
          is_active: typeof full?.is_active === 'boolean'
            ? full.is_active
            : Boolean(agent.is_active),
          email: full?.email || agent.email || '',
        });
      } catch (err) {
        console.error('Error cargando agente:', err);
        setSnackbar({ open: true, severity: 'error', message: 'Error al cargar información del agente' });
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, agent, getTokenSilent]);

  const handleToggleEdit = () => {
    if (!canEdit) return;
    setEditing((v) => !v);
  };

  const handleChange = (key) => (e) => {
    setLocalForm((prev) => ({ ...prev, [key]: e.target.value }));
  };

  const handleSave = async () => {
    if (!canEdit || !data?._id) return;
    setSaving(true);
    try {
      const token = await getTokenSilent();
      const payload = {
        display_name: localForm.display_name,
        role: localForm.role,
        subrole: localForm.subrole,
        branch: localForm.branch,
        agent_phone_number: localForm.agent_phone_number,
        agent_disponibility: localForm.agent_disponibility,
        is_active: localForm.is_active,
      };
      await updateAgentInformation({ id_agent: data._id, token, payload });
      setSnackbar({ open: true, severity: 'success', message: 'Agente actualizado exitosamente' });
      setEditing(false);
      if (onAgentUpdated) {
        onAgentUpdated({ ...data, ...payload });
      }
      onClose();
    } catch (error) {
      console.error('Error guardando agente:', error);
      const message = error?.response?.data?.message || error?.message || 'Error al guardar el agente';
      setSnackbar({ open: true, severity: 'error', message });
    } finally {
      setSaving(false);
    }
  };

  const executeDelete = async (newIdAgent) => {
    if (!data?._id) return;

    setDeleting(true);
    try {
      const token = await getTokenSilent();
      const response = await deleteAgent({
        id_agent: data._id,
        new_id_agent: newIdAgent ?? null,
        token,
      });

      setShowDelete(false);
      setShowReassign(false);
      onClose?.();

      if (onAgentDeleted) {
        onAgentDeleted({ deletedAgentId: data._id, response });
      }

      const successMessage = response?.message || 'Agente eliminado correctamente';
      if (onNotify) {
        onNotify(successMessage, 'success');
      } else {
        setSnackbar({ open: true, severity: 'success', message: successMessage });
      }
    } catch (error) {
      console.error('Error eliminando agente:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Error al eliminar el agente';
      if (onNotify) {
        onNotify(errorMessage, 'error');
      } else {
        setSnackbar({ open: true, severity: 'error', message: errorMessage });
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    const assignedContacts = Number(data?.assigned_contacts ?? agent?.assigned_contacts ?? 0);
    setShowDelete(false);

    if (assignedContacts > 0) {
      const infoMsg = 'No es posible eliminar. El agente cuenta con contactos asignados';
      if (onNotify) {
        onNotify(infoMsg, 'error');
      } else {
        setSnackbar({ open: true, severity: 'error', message: infoMsg });
      }
      setShowReassign(true);
      return;
    }

    await executeDelete(null);
  };

  const handleReassignAndDelete = async (newIdAgent) => {
    if (!newIdAgent) return;
    await executeDelete(newIdAgent);
  };

  const handleCloseSnackbar = () => setSnackbar((s) => ({ ...s, open: false }));

  return (
    <>
      <Dialog
        open={open}
        disableEscapeKeyDown
        onClose={(event, reason) => {
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
          onClose();
        }}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Grow}
        transitionDuration={500}
        PaperProps={{
          sx: {
            borderRadius: 2,
            marginTop: 10,
          },
          style: {
            zIndex: 1600,
          },
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pb: 1,
            pt: 0.5,
          }}
        >
          <Typography variant="h3" sx={{ py: 2 }}>
            {canEdit ? 'Editar Agente' : 'Ver Agente'}
          </Typography>
          <IconButton
            aria-label="cerrar"
            onClick={onClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
              '&:hover': {
                color: (theme) => theme.palette.grey[700],
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          sx={{
            pt: 1,
            pb: 3,
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box>
              <Box sx={{ mb: 2, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={localForm.display_name}
                  onChange={handleChange('display_name')}
                  size="small"
                  disabled={!editing}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField fullWidth label="Correo" value={localForm.email} size="small" disabled />
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="edit-role-label">Rol</InputLabel>
                  <Select
                    labelId="edit-role-label"
                    label="Rol"
                    value={localForm.role}
                    onChange={handleChange('role')}
                    disabled={!editing}
                  >
                    {ROLE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel id="edit-subrole-label">Subrol</InputLabel>
                  <Select
                    labelId="edit-subrole-label"
                    label="Subrol"
                    value={localForm.subrole}
                    onChange={handleChange('subrole')}
                    disabled={!editing}
                  >
                    {SUBROLE_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Sucursal"
                  value={localForm.branch || ''}
                  onChange={handleChange('branch')}
                  size="small"
                  disabled={!editing}
                />
              </Box>

              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  label="Telefono agente"
                  value={localForm.agent_phone_number || ''}
                  onChange={handleChange('agent_phone_number')}
                  size="small"
                  disabled={!editing}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="body2">Disponibilidad / Estado</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {localForm.agent_disponibility === 'Activo' ? 'Activo' : 'Inactivo'}
                  </Typography>
                </Box>
                <Switch
                  checked={localForm.agent_disponibility === 'Activo'}
                  onChange={(event) => {
                    const value = event.target.checked ? 'Activo' : 'Inactivo';
                    setLocalForm((prev) => ({ ...prev, agent_disponibility: value }));
                  }}
                  disabled={!editing}
                />
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: (theme) => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1,
                  px: 1.5,
                  py: 1,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography variant="body2">Acceso</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {localForm.is_active ? 'Activo' : 'Inactivo'}
                  </Typography>
                </Box>
                <Switch
                  checked={Boolean(localForm.is_active)}
                  onChange={(event) => {
                    setLocalForm((prev) => ({ ...prev, is_active: event.target.checked }));
                  }}
                  disabled={!editing}
                />
              </Box>

              

              {canEdit ? (
                <Box sx={{ mt: 1 }}>
                  <Button
                    fullWidth
                    onClick={() => setShowDelete(true)}
                    color="error"
                    variant="outlined"
                    disabled={deleting}
                    sx={{ py: 1, borderRadius: 2 }}
                  >
                    Eliminar agente
                  </Button>
                </Box>
              ) : null}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} variant="outlined" disabled={deleting || saving}>
            {canEdit ? 'Cancelar' : 'Cerrar'}
          </Button>
          {canEdit ? (
            <>
              <Box sx={{ flex: 1 }} />
              {!editing ? (
                <Button onClick={handleToggleEdit} variant="contained" disabled={deleting || saving}>
                  Editar
                </Button>
              ) : (
                <Button onClick={handleSave} variant="contained" disabled={saving}>
                  {saving ? 'Guardando...' : 'Guardar'}
                </Button>
              )}
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      <DeleteAgent open={showDelete} onClose={() => setShowDelete(false)} onConfirm={handleDeleteConfirm} agent={data || agent} />

      <ReassignContactsModal
        open={showReassign}
        onClose={() => setShowReassign(false)}
        onConfirm={handleReassignAndDelete}
        agents={agentsForReassign}
        currentAgentId={data?._id || agent?._id}
        loading={deleting}
      />

      <Snackbar open={snackbar.open} autoHideDuration={5000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

EditAgentInformation.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  agent: PropTypes.object,
  onAgentUpdated: PropTypes.func,
  onAgentDeleted: PropTypes.func,
  agentsForReassign: PropTypes.array,
  onNotify: PropTypes.func,
  canEdit: PropTypes.bool,
};
